import React from 'react';
import Icon from '../../../components/AppIcon';

const AIInsightsPanel = ({ tickets, isStaff }) => {
  const calculateInsights = () => {
    if (!tickets?.length) return null;

    const avgConfidence = tickets
      ?.filter(t => t?.ai_confidence_score)
      ?.reduce((sum, t) => sum + t?.ai_confidence_score, 0) / tickets?.length;

    const automatedCount = tickets?.filter(t => 
      t?.ai_analysis?.requires_human === false
    )?.length;

    const sentimentBreakdown = tickets?.reduce((acc, t) => {
      const sentiment = t?.ai_analysis?.sentiment || 'neutral';
      acc[sentiment] = (acc?.[sentiment] || 0) + 1;
      return acc;
    }, {});

    const categoryBreakdown = tickets?.reduce((acc, t) => {
      acc[t?.category] = (acc?.[t?.category] || 0) + 1;
      return acc;
    }, {});

    const topCategory = Object.entries(categoryBreakdown)
      ?.sort(([, a], [, b]) => b - a)?.[0];

    return {
      avgConfidence: Math.round(avgConfidence * 100),
      automatedCount,
      automationRate: Math.round((automatedCount / tickets?.length) * 100),
      sentimentBreakdown,
      topCategory: topCategory?.[0],
      topCategoryCount: topCategory?.[1]
    };
  };

  const insights = calculateInsights();

  if (!insights) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Sparkles" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">AI Insights</h3>
        </div>
        <p className="text-sm text-muted-foreground text-center py-8">
          No data available yet
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Icon name="Sparkles" size={20} className="text-primary" />
        <h3 className="font-semibold text-foreground">AI Insights</h3>
      </div>

      {/* AI Confidence */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">Avg AI Confidence</span>
          <span className="text-lg font-bold text-foreground">{insights?.avgConfidence}%</span>
        </div>
        <div className="w-full bg-background rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all"
            style={{ width: `${insights?.avgConfidence}%` }}
          />
        </div>
      </div>

      {/* Automation Rate */}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Zap" size={16} className="text-primary" />
          <span className="text-sm font-medium text-foreground">Automation Rate</span>
        </div>
        <p className="text-2xl font-bold text-primary">{insights?.automationRate}%</p>
        <p className="text-xs text-muted-foreground mt-1">
          {insights?.automatedCount} of {tickets?.length} tickets handled automatically
        </p>
      </div>

      {/* Sentiment Breakdown */}
      <div>
        <h4 className="text-sm font-medium text-foreground mb-3">Sentiment Analysis</h4>
        <div className="space-y-2">
          {Object.entries(insights?.sentimentBreakdown || {})?.map(([sentiment, count]) => {
            const colors = {
              positive: 'bg-green-500',
              neutral: 'bg-blue-500',
              negative: 'bg-orange-500',
              frustrated: 'bg-red-500'
            };
            const percentage = Math.round((count / tickets?.length) * 100);
            
            return (
              <div key={sentiment}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground capitalize">{sentiment}</span>
                  <span className="text-foreground font-medium">{count} ({percentage}%)</span>
                </div>
                <div className="w-full bg-background rounded-full h-1.5">
                  <div
                    className={`${colors?.[sentiment]} h-1.5 rounded-full transition-all`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Category */}
      {insights?.topCategory && (
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Most Common Issue</span>
          </div>
          <p className="text-lg font-semibold text-foreground capitalize">
            {insights?.topCategory?.replace('_', ' ')}
          </p>
          <p className="text-xs text-muted-foreground">
            {insights?.topCategoryCount} tickets
          </p>
        </div>
      )}

      {/* Quick Stats */}
      {isStaff && (
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-3">Quick Stats</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total Tickets</span>
              <span className="font-medium text-foreground">{tickets?.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Needs Attention</span>
              <span className="font-medium text-orange-500">
                {tickets?.filter(t => t?.status === 'open' || t?.priority === 'urgent')?.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIInsightsPanel;